<template>
  <div class="exam-results-container">
    <div v-if="loading" class="text-center">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p>Loading exam results...</p>
    </div>

    <div v-else class="results-content">
      <div class="exam-header mb-4">
        <h2>{{ examName }} - Results</h2>
      </div>

      <div v-if="examProgress" class="results-summary">
        <div class="row">
          <div class="col-md-6">
            <div class="card">
              <div class="card-body text-center">
                <h3 class="card-title">Your Score</h3>
                <div class="score-display">
                  <span class="score-number">{{ examProgress.questionsCorrect }}</span>
                  <span class="score-divider">/</span>
                  <span class="score-total">{{ examProgress.questionsTotal }}</span>
                </div>
                <div class="percentage">
                  {{ Math.round((examProgress.questionsCorrect / examProgress.questionsTotal) * 100) }}%
                </div>
              </div>
            </div>
          </div>
          
          <div class="col-md-6">
            <div class="card">
              <div class="card-body text-center">
                <h3 class="card-title">Result</h3>
                <div :class="resultClass" class="result-status">
                  {{ examProgress.completed ? (examProgress.passed ? 'PASSED' : 'FAILED') : 'IN PROGRESS' }}
                </div>
                <div v-if="passPercentage" class="pass-requirement">
                  (Required: {{ passPercentage }}%)
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="examProgress.instructorFeedback" class="feedback-section mt-4">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Instructor Feedback</h4>
              <p class="card-text">{{ examProgress.instructorFeedback }}</p>
            </div>
          </div>
        </div>

        <div class="completion-info mt-4">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Exam Information</h4>
              <div class="row">
                <div class="col-md-6">
                  <p><strong>Started:</strong> {{ formatDate(examProgress.createdAt) }}</p>
                  <p><strong>Completed:</strong> {{ formatDate(examProgress.updatedAt) }}</p>
                </div>
                <div class="col-md-6">
                  <p><strong>Questions Correct:</strong> {{ examProgress.questionsCorrect }}</p>
                  <p><strong>Questions Incorrect:</strong> {{ examProgress.questionsIncorrect }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="actions mt-4">
          <div class="d-flex gap-3">
            <a href="/content" class="btn btn-primary">Back to Courses</a>
            <a href="/grades" class="btn btn-outline-primary">View All Grades</a>
            <button 
              v-if="allowRetries && !examProgress.passed" 
              @click="retakeExam"
              class="btn btn-warning"
            >
              Retake Exam
            </button>
          </div>
        </div>
      </div>

      <div v-else class="no-results">
        <div class="alert alert-warning">
          <h4>No Results Found</h4>
          <p>No exam results were found. You may need to take the exam first.</p>
          <a :href="`/exams/${examId}`" class="btn btn-primary">Take Exam</a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';

export default {
  name: 'ExamResultsPage',
  props: {
    examId: {
      type: Number,
      required: true
    },
    examName: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const loading = ref(true);
    const examProgress = ref(null);
    const examInfo = ref(null);
    const allowRetries = ref(true);
    const passPercentage = ref(null);

    const resultClass = computed(() => {
      if (!examProgress.value) return '';
      if (!examProgress.value.completed) return 'text-warning';
      return examProgress.value.passed ? 'text-success' : 'text-danger';
    });

    const loadExamResults = async () => {
      try {
        const { data } = await axios.get(`/api/exams/${props.examId}/progress`);
        examProgress.value = data;
      } catch (error) {
        console.error('Error loading exam results:', error);
        examProgress.value = null;
      }
    };

    const loadExamInfo = async () => {
      try {
        const { data } = await axios.get(`/api/exams/${props.examId}`);
        examInfo.value = data;
        allowRetries.value = data.allowMultipleAttempts === 'true';
        passPercentage.value = data.passPercentage ? parseInt(data.passPercentage) : null;
      } catch (error) {
        console.error('Error loading exam info:', error);
      }
    };

    const retakeExam = async () => {
      if (confirm('Are you sure you want to retake this exam? This will reset your current progress.')) {
        try {
          await axios.delete(`/api/exams/${props.examId}/progress`);
          window.location.href = `/exams/${props.examId}`;
        } catch (error) {
          console.error('Error resetting exam:', error);
          alert('Failed to reset exam. Please try again.');
        }
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    onMounted(async () => {
      try {
        await Promise.all([
          loadExamResults(),
          loadExamInfo()
        ]);
      } catch (error) {
        console.error('Error initializing exam results page:', error);
      } finally {
        loading.value = false;
      }
    });

    return {
      loading,
      examProgress,
      examInfo,
      allowRetries,
      passPercentage,
      resultClass,
      retakeExam,
      formatDate
    };
  }
};
</script>

<style scoped>
.exam-results-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
}

.score-display {
  font-size: 3rem;
  font-weight: bold;
  color: #007bff;
}

.score-number {
  color: #28a745;
}

.score-divider {
  color: #6c757d;
  margin: 0 10px;
}

.score-total {
  color: #6c757d;
}

.percentage {
  font-size: 1.5rem;
  color: #6c757d;
  margin-top: 10px;
}

.result-status {
  font-size: 2rem;
  font-weight: bold;
  margin: 15px 0;
}

.pass-requirement {
  color: #6c757d;
  font-size: 0.9rem;
}

.feedback-section {
  margin-top: 30px;
}

.completion-info {
  margin-top: 30px;
}

.actions {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #dee2e6;
}

.d-flex {
  display: flex;
}

.gap-3 {
  gap: 1rem;
}

.text-success {
  color: #28a745 !important;
}

.text-danger {
  color: #dc3545 !important;
}
</style>